import { Outlet, NavLink, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useI18nStore } from '@/store/i18nStore';
import ThemeToggle from '@/components/ThemeToggle';
import LanguageSelector from '@/components/LanguageSelector';
import NotificationBell from '@/components/NotificationBell';
import AppFooter from '@/components/AppFooter';
import api from '@/services/api';
import CityLogo from '@/components/CityLogo';
import {
  LayoutDashboard,
  Building2,
  Package,
  Wallet,
  Users,
  CreditCard,
  MessageCircle,
  Trophy,
  UserCircle,
  Settings,
  LogOut,
  TicketCheck,
  type LucideIcon,
} from 'lucide-react';

interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
}

const navItems: NavItem[] = [
  { to: '/dashboard',    labelKey: 'nav.dashboard',    icon: LayoutDashboard },
  { to: '/join',         labelKey: 'nav.join',         icon: Building2 },
  { to: '/boxes',        labelKey: 'nav.boxes',        icon: Package },
  { to: '/ledger',       labelKey: 'nav.ledger',       icon: Wallet },
  { to: '/referral',     labelKey: 'nav.referral',     icon: Users },
  { to: '/subscription', labelKey: 'nav.subscription', icon: CreditCard },
  { to: '/chat',         labelKey: 'nav.chat',         icon: MessageCircle },
  { to: '/tickets',      labelKey: 'nav.tickets',      icon: TicketCheck },
  { to: '/badges',       labelKey: 'nav.badges',       icon: Trophy },
  { to: '/profile',      labelKey: 'nav.profile',      icon: UserCircle },
];

const adminNavItem: NavItem = { to: '/admin', labelKey: 'nav.admin', icon: Settings };

/* ── Tooltip wrapper ────────────────────────── */
function NavTooltip({ label, children }: { label: string; children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  return (
    <span
      className="relative"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2.5 py-1 text-xs font-medium rounded-lg whitespace-nowrap z-50 bg-surface-900 text-white dark:bg-white dark:text-surface-900 shadow-lg pointer-events-none animate-in fade-in duration-150">
          {label}
        </span>
      )}
    </span>
  );
}

export default function MainLayout() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const t = useI18nStore((s) => s.t);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const allNavItems = isAdmin ? [...navItems, adminNavItem] : navItems;

  // Chat unread count for badge
  const { data: unreadData } = useQuery({
    queryKey: ['chat-unread'],
    queryFn: () => api.get('/chat/unread').then((r) => r.data),
    refetchInterval: 15000,
  });
  const chatUnread: number = unreadData?.unread ?? 0;

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-white dark:bg-[#1b1b1f] transition-colors">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white/80 dark:bg-[#1b1b1f]/80 backdrop-blur-lg border-b border-surface-200 dark:border-surface-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <Link to="/dashboard" className="flex items-center gap-2 no-underline">
                <CityLogo size={28} variant="small" className="text-primary-500" />
                <span className="font-bold text-lg text-surface-900 dark:text-white">React-City</span>
              </Link>

              {/* Nav — icon only + tooltip */}
              <nav className="hidden md:flex items-center gap-0.5" aria-label="Navigazione principale">
                {allNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavTooltip key={item.to} label={t(item.labelKey)}>
                      <NavLink
                        to={item.to}
                        className={({ isActive }) =>
                          `relative flex items-center justify-center w-10 h-10 rounded-xl transition-colors no-underline ${
                            isActive
                              ? 'bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400'
                              : 'text-surface-500 hover:text-surface-900 hover:bg-surface-100 dark:text-surface-400 dark:hover:text-white dark:hover:bg-surface-800'
                          }`
                        }
                      >
                        <Icon className="w-5 h-5" strokeWidth={1.75} />
                        {item.to === '/chat' && chatUnread > 0 && (
                          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                            {chatUnread > 99 ? '99+' : chatUnread}
                          </span>
                        )}
                      </NavLink>
                    </NavTooltip>
                  );
                })}
              </nav>

              {/* User menu */}
              <div className="flex items-center gap-2">
                <NotificationBell />
                <LanguageSelector />
                <ThemeToggle />
                <span className="text-sm text-surface-500 dark:text-surface-400 hidden lg:inline max-w-[120px] truncate">
                  {user?.full_name ?? 'Utente'}
                </span>
                <button
                  onClick={logout}
                  className="flex items-center justify-center w-9 h-9 rounded-xl text-surface-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                  aria-label="Esci"
                  title={t('nav.logout')}
                >
                  <LogOut className="w-4.5 h-4.5" strokeWidth={1.75} />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile nav — icons + small label */}
        <nav
          className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white dark:bg-[#1b1b1f] border-t border-surface-200 dark:border-surface-800 safe-area-inset-bottom"
          aria-label="Navigazione mobile"
        >
          <div className="grid grid-cols-5 gap-0 py-1.5 px-1">
            {allNavItems.slice(0, 5).map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `relative flex flex-col items-center gap-0.5 py-1 text-[10px] font-medium no-underline ${
                      isActive ? 'text-primary-500' : 'text-surface-400 dark:text-surface-500'
                    }`
                  }
                >
                  <Icon className="w-5 h-5" strokeWidth={1.75} />
                  {item.to === '/chat' && chatUnread > 0 && (
                    <span className="absolute top-0 right-1/4 inline-flex items-center justify-center min-w-[14px] h-3.5 px-0.5 text-[9px] font-bold text-white bg-red-500 rounded-full">
                      {chatUnread > 99 ? '99+' : chatUnread}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
          {allNavItems.length > 5 && (
            <div className="grid grid-cols-5 gap-0 pb-1.5 px-1">
              {allNavItems.slice(5).map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `relative flex flex-col items-center gap-0.5 py-1 text-[10px] font-medium no-underline ${
                        isActive ? 'text-primary-500' : 'text-surface-400 dark:text-surface-500'
                      }`
                    }
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                    {item.to === '/chat' && chatUnread > 0 && (
                      <span className="absolute top-0 right-1/4 inline-flex items-center justify-center min-w-[14px] h-3.5 px-0.5 text-[9px] font-bold text-white bg-red-500 rounded-full">
                        {chatUnread > 99 ? '99+' : chatUnread}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          )}
        </nav>

        {/* Content */}
        <main className="flex-1 pb-20 md:pb-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <Outlet />
          </div>
        </main>

        {/* Footer */}
        <div className="hidden md:block">
          <AppFooter />
        </div>
      </div>
    </>
  );
}
